import React from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import queryString from 'qs';
import { Radio, Message, Divider } from 'semantic-ui-react';
import { Field, reduxForm } from 'redux-form/immutable';
import _ from 'lodash';

import { authService } from '../../utils/auth-service';
import TextInput from '../../components/form-input-text';

import { EMAIL_REGULAR_EXPRESSION } from '../../utils/form-input-validation';
import { reportError } from '../../utils/error-handling-service';
import { trackEvent } from '../../utils/metric-analytics-service';

import ThemeSelector from '../../themes/theme-selector';

const theme = ThemeSelector.theme();
const TERMS_LINK = ThemeSelector.getTermsAndConditionsLink();

const trimAndSplit = name => {
  const trimmedName = _.trim(name);
  const splitFullname = _.split(trimmedName, /\s+/);
  return splitFullname;
};

class Signup extends React.Component {
  static contextTypes = {
    router: PropTypes.object,
  };

  constructor(props, context) {
    super(props, context);

    const { location } = this.props;

    const qs = location.search;
    const parsed = queryString.parse(_.replace(qs, '?', ''));
    const qsEmail = parsed.email;
    const qsName = parsed.name;

    if (qsEmail) {
      this.props.change('email', qsEmail);
    }

    if (qsName) {
      this.props.change('fullname', qsName);
    }

    this.state = {
      signupError: null,
      modal: false,
      checked: false,
      submittedData: null,
    };
  }

  getAuthParams = data => {
    const fullname = data.get('fullname');
    const splitFullname = trimAndSplit(fullname);
    const firstname = _.head(_.take(splitFullname));
    const lastname = _.join(_.slice(splitFullname, 1), ' ');
    return {
      connection: 'Username-Password-Authentication',
      email: data.get('email'),
      password: data.get('password'),
      scope: 'openid app_metadata',
      user_metadata: {
        firstname,
        lastname,
      },
    };
  }

  showModal = () => {
    this.setState({ modal: true });
  }

  acceptModal = () => {
    this.setState({ modal: false });
    this.googleSignup();
  }

  hideModal = () => {
    this.setState({ modal: false });
  }

  signUp = data => {
    const context = this;

    const signUpData = {
      name: data.get('fullname'),
      email: data.get('email'),
      signupType: 'emailPassword',
    };
    trackEvent('Sign Up', signUpData);

    this.setState({
      submittedData: signUpData,
    }, () => {
      authService.signup(this.getAuthParams(data), error => {
        if (error) {
          reportError('SIGNUP_ERROR', error.description);
          context.setState({ signupError: error.description });
        }
      });
    });
  }

  googleSignupValidate = () => {
    if (this.props.email) {
      this.showModal();
    } else {
      this.googleSignup();
    }
  }

  googleSignup = () => {
    trackEvent('Sign Up', { signupType: 'google' });
    authService.socialLogin({
      connection: 'google-oauth2',
      scope: 'openid app_metadata',
    }, error => {
      if (error) {
        reportError('GOOGLE_SIGNUP_ERROR', error.description);
        this.setState({ signupError: error.description });
      }
    });
  }

  render() {
    const { handleSubmit, email } = this.props;
    const { signupError, modal, submittedData } = this.state;

    return (
      <div>
        <div className={modal ? 'page-centered blurred' : 'page-centered'}>
          <div className="center-outer">
            <div className="center-inner">
              <div className="center-logo">
                <a onClick={() => this.context.router.push('/')}>
                  <img src={theme.darkLogo.src} alt={theme.darkLogo.alt} />
                </a>
              </div>
              { signupError && _.includes(signupError, 'already exist') ?
                <div className="center-content">
                  <Message
                    icon="user outline"
                    info
                    header="It looks like you already have an account with us"
                    content={
                      <div>
                        <Divider hidden />
                        <p>{submittedData.email} is already registered</p>
                        <p>Click the button below to log in instead...</p>
                      </div>
                    }
                  />
                  <div className="spacer" />
                  <button className="large rose stretch" onClick={() => this.context.router.push('/login')}>
                    <p>Log In</p>
                  </button>
                </div>
                :
                <div className="center-content">
                  <button className="large stretch" onClick={this.googleSignupValidate}>
                    <p>Sign up with <img className="google" alt="Google logo" src="/logos/google-logo.svg" /></p>
                  </button>
                  <form onSubmit={handleSubmit(this.signUp)}>
                    <div className="option-separator">
                      <div className="left" />
                      <div className="center">
                        <p>or</p>
                      </div>
                      <div className="right" />
                    </div>
                    <Field
                      name="fullname"
                      type="text"
                      component={TextInput}
                      img="/icons/user.svg"
                      placeholder="Full name"
                    />
                    <Field
                      name="email"
                      type="text"
                      component={TextInput}
                      img="/icons/email.svg"
                      disabled={!!email}
                      placeholder="Email address"
                    />
                    <Field
                      name="password"
                      type="password"
                      component={TextInput}
                      img="/icons/password.svg"
                      placeholder="Choose a password"
                    />
                    <div className="flex">
                      <div className="item">
                        <Radio
                          toggle checked={this.state.checked}
                          onChange={() => this.setState({ checked: !this.state.checked })}
                        />
                      </div>
                      <div className="space">
                        <p className="radio-label">
                          {'I agree to the '}
                          <a
                            href={TERMS_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                          Terms and Conditions
                          </a>
                        .</p>
                      </div>
                    </div>
                    {
                      signupError &&
                      <Message
                        icon="ban"
                        error
                        header="There was a problem signing up"
                        content={signupError}
                      />
                    }
                    <button type="submit" className="large rose stretch" disabled={!this.state.checked}>
                      <p>Sign up</p>
                    </button>
                  </form>
                  <p className="info">
                    Already with us? <a onClick={() => this.context.router.push('/login')}>Log in</a>.
                  </p>
                </div>
              }
            </div>
          </div>
        </div>
        <div className={modal ? 'custom-modal visible' : 'custom-modal'}>
          <div className="modal-content">
            <div className="top">
              <h3>Signing in with Google?</h3>
            </div>
            <div className="modal-close">
              <img src="/icons/close.svg" alt="close" onClick={this.hideModal} />
            </div>
            <div className="center">
              <p>
                Please use the google account associated with the email address we sent your invite to:&nbsp;
                <strong>{email}</strong>.
              </p>
              <div className="spacer" />
              <div className="spacer" />
              <button className="large rose stretch" onClick={this.acceptModal}>
                <p>Sign In</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Signup.propTypes = {
  email: PropTypes.string,
  location: PropTypes.object.isRequired,
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

Signup.defaultProps = {
  routeParams: null,
  email: null,
};

const validate = values => {
  const errors = {};

  const fullname = values.get('fullname');
  const email = values.get('email');
  const password = values.get('password');

  if (!fullname) errors.fullname = 'Enter your full name.';
  if (fullname) {
    const splitFullname = trimAndSplit(fullname);
    if (splitFullname.length < 2) errors.fullname = 'Enter both your first and last name.';
  }

  if (!email) errors.email = 'Enter your email address.';
  if (email && !EMAIL_REGULAR_EXPRESSION.test(email)) errors.email = 'Enter a valid email address.';

  if (!password) errors.password = 'Enter a new password.';
  if (password && (password.length < 8 || !password.match(/\d+/g))) {
    errors.password = 'Password must have at least 8 characters and contain 1 number.';
  }

  return errors;
};

const mapStateToProps = state => ({
  shortId: state.invite.get('shortId'),
  email: state.invite.get('email'),
  initialValues: {
    fullname: state.invite.get('name'),
    email: state.invite.get('email'),
  },
});

export default connect(mapStateToProps, null)(
  reduxForm({
    form: 'SignupForm',
    validate,
  })(Signup),
);



/** WEBPACK FOOTER **
 ** ./src/containers/signup/index.js
 **/